import React from 'react';
// The build error suggests a dependency issue. We keep the standard imports 
// required for React PDF, assuming the host environment loads them correctly 
// at runtime, as is common for PDF generation functionality.
import { Page, View, Text, Document, StyleSheet, Font } from '@react-pdf/renderer';

// Utility to format numbers for the PDF context
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 20,
        fontSize: 9,
        fontFamily: 'Helvetica', // Standard PDF font
    },
    title: {
        fontSize: 16,
        marginBottom: 10,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    table: {
        display: 'flex',
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tableRow: {
        margin: 'auto',
        flexDirection: 'row',
        height: 18, // DETAIL_ROW_HEIGHT constant
        borderBottomColor: '#ccc',
        borderBottomWidth: 0.5,
    },
    tableHeader: {
        backgroundColor: '#e9e9e9',
        borderBottomWidth: 1.5,
        borderBottomColor: '#000',
        fontWeight: 'bold',
        height: 25,
    },
    columnHeader: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    detailCell: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 3,
        overflow: 'hidden',
    },

    // Column Definitions (Total: 8 columns, ensuring widths sum to 100%)
    colInvNo: { width: '8%', },
    colInvDate: { width: '10%', },
    colCustomer: { width: '25%', },
    colSalesMan: { width: '12%', },
    colAccount: { width: '15%', },
    colCashCredit: { width: '10%', },
    colAmountExclVAT: { width: '10%', textAlign: 'right', },
    colAmount: { width: '10%', textAlign: 'right', },

    // Grouping Styles (spanning multiple columns)
    groupRow: {
        backgroundColor: '#e0f7fa',
        fontWeight: 'bold',
        height: 20,
    },
    subRow: {
        backgroundColor: '#f0f4c3',
        fontWeight: 'semibold',
        height: 20,
        paddingLeft: 10,
    },
    subSubRow: {
        backgroundColor: '#fff8e1',
        fontWeight: 'medium',
        height: 20,
        paddingLeft: 20,
    },
    totalRow: {
        backgroundColor: '#b3b3b3',
        borderTopWidth: 2,
        borderTopColor: '#000',
        height: 25,
        fontWeight: 'bold',
    },
    groupText: {
        paddingTop: 3,
        paddingLeft: 5,
    }
});

const renderHeader = () => (
    <View style={[styles.tableRow, styles.tableHeader]} fixed>
        <Text style={[styles.columnHeader, styles.colInvNo]}>INV NO</Text>
        <Text style={[styles.columnHeader, styles.colInvDate]}>INV DATE</Text>
        <Text style={[styles.columnHeader, styles.colCustomer]}>CUSTOMER</Text>
        <Text style={[styles.columnHeader, styles.colSalesMan]}>SALES MAN</Text>
        <Text style={[styles.columnHeader, styles.colAccount]}>ACCOUNT</Text>
        <Text style={[styles.columnHeader, styles.colCashCredit]}>CASH/CREDIT</Text>
        <Text style={[styles.columnHeader, styles.colAmountExclVAT]}>AMT EXCLVAT</Text>
        <Text style={[styles.columnHeader, styles.colAmount]}>AMOUNT</Text>
    </View>
);

// Main Component that renders the body rows
const SalesTableGroupedPDFContent = ({ rows, grandColumnTotals }) => {

    const renderRow = (row, index) => {
        const isGroupHeader = row.isGroupHeader;
        const isSubHeader = row.isSubHeader;
        const isSubSubHeader = row.isSubSubHeader;
        const isDetailRow = !isGroupHeader && !isSubHeader && !isSubSubHeader;

        // 1. Group Header (Department Total)
        if (isGroupHeader) {
            return (
                <View key={`dept-header-${index}`} style={[styles.tableRow, styles.groupRow]} wrap={false}>
                    {/* FIX 1: Remove right border from 80% spanning cell */}
                    <Text style={[styles.detailCell, styles.groupText, { width: '80%', fontWeight: 'bold', borderRightWidth: 0 }]}>
                        {`Department Total: ${row.DEPTNO}`}
                    </Text>
                    {/* FIX 2: Explicitly add left border to amount cells */}
                    <Text style={[styles.detailCell, styles.colAmountExclVAT, { fontWeight: 'bold', borderLeftWidth: 1 }]}>{formatNumber(row.totalAmountExclVAT)}</Text>
                    <Text style={[styles.detailCell, styles.colAmount, { fontWeight: 'bold', borderLeftWidth: 1 }]}>{formatNumber(row.totalAmount)}</Text>
                </View>
            );
        }

        // 2. Sub Header (Type Sub-Total)
        if (isSubHeader) {
            return (
                <View key={`type-header-${index}`} style={[styles.tableRow, styles.subRow]} wrap={false}>
                    {/* FIX 1: Remove right border from 80% spanning cell */}
                    <Text style={[styles.detailCell, styles.groupText, { width: '80%', fontWeight: 'semibold', borderRightWidth: 0 }]}>
                        {`Type Sub-Total: ${row.TYPE}`}
                    </Text>
                    {/* FIX 2: Explicitly add left border to amount cells */}
                    <Text style={[styles.detailCell, styles.colAmountExclVAT, { fontWeight: 'semibold', borderLeftWidth: 1 }]}>{formatNumber(row.totalAmountExclVAT)}</Text>
                    <Text style={[styles.detailCell, styles.colAmount, { fontWeight: 'semibold', borderLeftWidth: 1 }]}>{formatNumber(row.totalAmount)}</Text>
                </View>
            );
        }

        // 3. Sub-Sub Header (CASH/CREDIT Sub-Total)
        if (isSubSubHeader) {
            return (
                <View key={`cashcred-header-${index}`} style={[styles.tableRow, styles.subSubRow]} wrap={false}>
                    {/* FIX 1: Remove right border from 80% spanning cell */}
                    <Text style={[styles.detailCell, styles.groupText, { width: '80%', fontWeight: 'medium', borderRightWidth: 0 }]}>
                        {`CASH/CREDIT Sub-Total: ${row.CASHCRED}`}
                    </Text>
                    {/* FIX 2: Explicitly add left border to amount cells */}
                    <Text style={[styles.detailCell, styles.colAmountExclVAT, { fontWeight: 'medium', borderLeftWidth: 1 }]}>{formatNumber(row.totalAmountExclVAT)}</Text>
                    <Text style={[styles.detailCell, styles.colAmount, { fontWeight: 'medium', borderLeftWidth: 1 }]}>{formatNumber(row.totalAmount)}</Text>
                </View>
            );
        }

        // Detail Row (Transactional) - 8 columns
        if (isDetailRow) {
            return (
                <View key={`detail-row-${index}`} style={styles.tableRow} wrap={false}>
                    <Text style={[styles.detailCell, styles.colInvNo]}>{row.INVNO}</Text>
                    <Text style={[styles.detailCell, styles.colInvDate]}>{row.INV_DATE}</Text>
                    <Text style={[styles.detailCell, styles.colCustomer]}>{row.CUSTOMER}</Text>
                    <Text style={[styles.detailCell, styles.colSalesMan]}>{row['SALES MAN']}</Text>
                    <Text style={[styles.detailCell, styles.colAccount]}>{row.ACCOUNT}</Text>
                    <Text style={[styles.detailCell, styles.colCashCredit]}>{row.CASHCRED}</Text>
                    <Text style={[styles.detailCell, styles.colAmountExclVAT]}>{formatNumber(row.AMOUNT_EXCLVAT)}</Text>
                    <Text style={[styles.detailCell, styles.colAmount]}>{formatNumber(row.AMOUNT)}</Text>
                </View>
            );
        }
        return null;
    };

    const renderFooter = () => {
        if (!grandColumnTotals || Object.keys(grandColumnTotals).length === 0) return null;
        
        // Grand Total Row
        return (
            <View style={[styles.tableRow, styles.totalRow]} fixed>
                {/* FIX 1: Remove right border from 80% spanning cell */}
                <Text style={[styles.detailCell, { width: '80%', textAlign: 'right', fontWeight: 'bold', borderRightWidth: 0 }]}>
                    GRAND TOTAL:
                </Text>
                {/* FIX 2: Explicitly add left border to amount cells */}
                <Text style={[styles.detailCell, styles.colAmountExclVAT, { fontWeight: 'bold', borderLeftWidth: 1 }]}>
                    {formatNumber(grandColumnTotals['AMOUNT_EXCLVAT'])}
                </Text>
                <Text style={[styles.detailCell, styles.colAmount, { fontWeight: 'bold', borderLeftWidth: 1 }]}>
                    {formatNumber(grandColumnTotals['AMOUNT'])}
                </Text>
            </View>
        );
    }

    return (
        <View style={styles.table}>
            {renderHeader()}
            <View>
                {/* Use a wrapper view for the body to enable proper page breaking */}
                {rows.map(renderRow)}
            </View>
            {renderFooter()}
        </View>
    );
}

// Wrap the content into a Document component
const SalesTableGroupedPDF = ({ rows, grandColumnTotals, filters }) => (


    <Document title="Grouped Sales Analysis Report">
        <Page size="A4" style={styles.page} orientation="landscape">
            <Text style={styles.title}>Grouped Sales Transaction Report</Text>

            {/* Filter Summary */}
            <View style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 8 }}>
                    Filters: Date Range ({filters?.startDate || 'N/A'} to {filters?.endDate || 'N/A'})
                    | Department: {filters?.departmentName || 'All'}
                    | Group: {filters?.groupName || 'All'}
                </Text>
            </View>

            <SalesTableGroupedPDFContent rows={rows} grandColumnTotals={grandColumnTotals} />

            <Text style={{ position: 'absolute', bottom: 10, right: 20, fontSize: 7 }} fixed>
                Page <Text render={({ pageNumber, totalPages }) => (`${pageNumber} of ${totalPages}`)} />
            </Text>
        </Page>
    </Document>
);

export default SalesTableGroupedPDF;